import {
  LayoutDashboard, Map, Video, AlertTriangle,
  ScanLine, Layers, Users, BarChart2, Settings,
  ShieldCheck, Activity, Radio, HeartPulse, LayoutTemplate,
  Flame,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import copecLogo from '../assets/copec-logo.svg'

// Branding por subdominio (2026-06-21). Cada tenant productivo tiene su
// propio host; mapeamos a un PNG servido por Caddy desde /branding/.
// Si no hay match, default al SVG Copec.
function tenantLogo() {
  if (typeof window === 'undefined') return { src: copecLogo, alt: 'Copec', title: 'CAD Global' }
  const h = window.location.hostname
  if (h === 'hites.retaliq.net')
    return { src: '/branding/hites.png',    alt: 'Hites',    title: 'Hites — CAD Global' }
  if (h === 'quipasur.retaliq.net')
    return { src: '/branding/quipasur.png', alt: 'Quipasur', title: 'Quipasur — CAD Global' }
  if (h === 'cad.retaliq.net')
    return { src: '/branding/retaliq.png',  alt: 'RetaliQ',  title: 'RetaliQ Incident Manager' }
  return { src: copecLogo, alt: 'Copec', title: 'Copec — CAD Global' }
}

// Deteccion de host cement para traducir labels al inglés en la demo India.
const isCementDemo = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'

// Cement demo — quitamos ANPR Search, People (Personas) y Floor Analysis
// porque no aplican a una planta industrial cementera.
const NAV = isCementDemo ? [
  { id: 'dashboard',  Icon: LayoutDashboard, label: 'Dashboard',     always: true },
  { id: 'map',        Icon: Map,              label: 'Network Map',   op: true },
  { id: 'monitor',    Icon: Video,            label: 'Live Monitor',  op: true },
  { id: 'incidents',  Icon: AlertTriangle,    label: 'Incidents',     op: true },
  null,
  { id: 'detections', Icon: Layers,           label: 'Detections',    report: true },
  { id: 'reports',    Icon: BarChart2,        label: 'Reports',       always: true },
  { id: 'audit',      Icon: Activity,         label: 'Audit',         manage: true },
  { id: 'edgehealth', Icon: HeartPulse,       label: 'Edge Health',   manage: true },
  { id: 'siteconfig', Icon: LayoutTemplate,   label: 'Site Config',   manage: true },
  null,
  { id: 'ptt',        Icon: Radio,            label: 'Radio PTT',     op: true, ptt: true },
  { id: 'settings',   Icon: Settings,         label: 'Settings',      always: true },
] : [
  { id: 'dashboard',  Icon: LayoutDashboard, label: 'Dashboard',     always: true },
  { id: 'map',        Icon: Map,              label: 'Mapa de red',   op: true },
  { id: 'monitor',    Icon: Video,            label: 'Monitor',       op: true },
  { id: 'incidents',  Icon: AlertTriangle,    label: 'Incidentes',    op: true },
  null,
  { id: 'lpr',        Icon: ScanLine,         label: 'Búsqueda LPR',  op: true },
  { id: 'detections', Icon: Layers,           label: 'Detecciones',   report: true },
  { id: 'persons',    Icon: Users,            label: 'Personas',      report: true },
  { id: 'reports',    Icon: BarChart2,        label: 'Reportes',      always: true },
  { id: 'analysis',   Icon: Flame,            label: 'Análisis del piso', report: true },
  { id: 'audit',      Icon: Activity,         label: 'Auditoría',     manage: true },
  { id: 'edgehealth', Icon: HeartPulse,       label: 'Salud Edge',    manage: true },
  { id: 'siteconfig', Icon: LayoutTemplate,   label: 'Config sitio',  manage: true },
  null,
  { id: 'ptt',        Icon: Radio,            label: 'Radio PTT',     op: true, ptt: true },
  { id: 'settings',   Icon: Settings,         label: 'Configuración', always: true },
]

const s = {
  sidebar: {
    width: 'var(--sidebar-w)', height: '100%',
    background: 'var(--bg-surface)',
    borderRight: '1px solid var(--border)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', padding: '8px 0', gap: 2,
    flexShrink: 0,
  },
  logo: {
    width: 44, height: 44,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: 6, userSelect: 'none', padding: '4px 2px',
    borderRadius: 'var(--radius-md)',
    background: '#fff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
  },
  btn: (active) => ({
    width: 36, height: 36, borderRadius: 'var(--radius-md)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', border: 'none', position: 'relative',
    background: active ? 'var(--bg-active)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--text-muted)',
    transition: 'background 0.15s, color 0.15s',
  }),
  divider: {
    width: 24, height: 1, background: 'var(--border)', margin: '4px 0',
  },
  spacer: { flex: 1 },
}

export default function NavSidebar({ active, onNav, pttActive }) {
  const user = useAuthStore(st => st.user)

  return (
    <aside style={s.sidebar}>
      {(() => {
        const lg = tenantLogo()
        return (
          <div style={s.logo} title={lg.title}>
            <img
              src={lg.src} alt={lg.alt}
              onError={(e) => { e.currentTarget.src = copecLogo }}
              style={{ maxWidth: 40, maxHeight: 40, width: 'auto', height: 'auto', objectFit: 'contain' }}
            />
          </div>
        )
      })()}

      {NAV.map((item, i) => {
        if (!item) return <div key={i} style={s.divider} />
        if (item.op     && !user?.can_operate)      return null
        if (item.report && !user?.can_view_reports)  return null
        if (item.manage && !user?.can_manage)        return null
        const { Icon } = item
        const isActive = active === item.id || (item.ptt && pttActive)
        return (
          <button
            key={item.id}
            style={{
              ...s.btn(isActive),
              // Radio PTT: destello cuando está activo en llamada
              ...(item.ptt && pttActive ? {
                background: 'var(--danger-light)',
                color: 'var(--danger)',
              } : {}),
            }}
            title={item.label}
            onClick={() => onNav(item.id)}
          >
            <Icon size={17} strokeWidth={1.75} />
            {/* Dot de actividad para PTT */}
            {item.ptt && pttActive && (
              <span style={{
                position: 'absolute', top: 5, right: 5,
                width: 6, height: 6, borderRadius: '50%',
                background: 'var(--danger)',
              }} />
            )}
          </button>
        )
      })}

      <div style={s.spacer} />
    </aside>
  )
}
